'use client';

import { BelowMedium } from '@lichtblick/theme';
import { useTracker } from '@lichtblick/tracker';
import classNames from 'classnames';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import aiLabelStyles from '../../helpers/ai-label.module.css';
import type { AVideoType, VideoSourceType } from '../../types/storyblok';
import { AImage } from '../AImage';
import { getImageDimensions } from '../AImage/Picture.helpers';
import styles from './AVideo.module.css';

const isValid = (source: VideoSourceType | undefined) =>
  Boolean(source?.poster?.filename || source?.webM?.filename || source?.mp4?.filename);

export type AVideoProps = AVideoType & {
  className?: string;
  shouldPlayInBackground?: boolean;
};

export const AVideo: FC<AVideoProps> = ({
  altText,
  autoplay = false,
  className,
  isMobileImageOnly,
  shouldPlayInBackground = false,
  videoSourceDesktop,
  videoSourceMobile,
}) => {
  const [isMobile, setIsMobile] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const isAutoPaused = useRef(false);
  const resumeAt = useRef(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { trackCustomEvent } = useTracker();

  const source = useMemo<VideoSourceType | undefined>(() => {
    if (!isMobile) {
      return [videoSourceDesktop?.[0], videoSourceMobile[0]].filter(isValid)[0];
    } else if (!isMobileImageOnly) {
      return videoSourceMobile[0];
    }
  }, [isMobile, isMobileImageOnly, videoSourceDesktop, videoSourceMobile]);

  const [showPlaceholder, setShowPlaceholder] = useState(!autoplay && !shouldPlayInBackground);

  useEffect(() => {
    const mediaQueryObserver = window.matchMedia(BelowMedium);
    const intersectionObserver = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting));
    const handleMediaQueryChange = () => setIsMobile(mediaQueryObserver.matches);
    const video = videoRef.current;

    mediaQueryObserver.addEventListener('change', handleMediaQueryChange);
    handleMediaQueryChange();

    if (video) {
      intersectionObserver.observe(video);
    }

    return () => {
      mediaQueryObserver.removeEventListener('change', handleMediaQueryChange);
      intersectionObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      const isPaused = video.paused; // must be checked before `load()`

      resumeAt.current = video.currentTime;
      video.load();
      video.currentTime = resumeAt.current;

      if (!isPaused) {
        video.play();
      }
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      isAutoPaused.current ||= shouldPlayInBackground || autoplay;

      if (!isVisible) {
        isAutoPaused.current ||= !video.paused;
        video.pause();
      } else if (isAutoPaused.current) {
        isAutoPaused.current = false;
        video.currentTime = resumeAt.current;
        video.play();
      }
    }
  }, [isVisible, shouldPlayInBackground, autoplay]);

  const placeholderClickHandler = useCallback(() => {
    trackCustomEvent({
      customProps: {
        navigation_area: 'Body',
        navigation_element: source?.webM?.filename ? source.webM?.filename : source?.mp4.filename,
      },
      eventName: 'navigation',
    });

    setShowPlaceholder(false);
  }, [source?.mp4.filename, source?.webM?.filename, trackCustomEvent]);

  const saveCurrentTime = () => {
    resumeAt.current = videoRef.current?.currentTime ?? 0;
  };

  const posterDimensions = source?.poster.filename ? (getImageDimensions(source?.poster.filename) ?? []) : [];
  const aiLabel = source?.webM?.meta_data?.ai_label?.trim() || source?.mp4?.meta_data?.ai_label?.trim();

  return (
    <>
      {isMobileImageOnly && (
        <AImage
          altText={altText ?? ''}
          className={classNames(styles.image, shouldPlayInBackground && styles['image-is-background'], className)}
          imageMobile={videoSourceMobile[0].poster}
        />
      )}
      {showPlaceholder ? (
        <button aria-label="Video abspielen" type="button" className={styles.button} onClick={placeholderClickHandler}>
          <AImage
            altText={altText ?? ''}
            imageDesktop={videoSourceDesktop?.[0]?.poster}
            imageMobile={videoSourceMobile[0].poster}
          />
        </button>
      ) : (
        <div
          className={classNames(
            styles.video,
            isValid(source) && styles['video-is-visible'],
            shouldPlayInBackground && styles['video-is-background'],
            aiLabelStyles['ai-label'],
            className,
          )}
          data-ai-label={aiLabel}
        >
          <video
            autoPlay={true}
            controls={!shouldPlayInBackground}
            height={posterDimensions[1]}
            loop={shouldPlayInBackground || autoplay}
            muted={shouldPlayInBackground || autoplay}
            onPause={saveCurrentTime}
            onSeeked={saveCurrentTime}
            playsInline
            poster={source?.poster.filename && `${source?.poster.filename}/m/`}
            preload="none"
            ref={videoRef}
            style={{
              aspectRatio: `${posterDimensions[0]}/${posterDimensions[1]}`,
            }}
            width={posterDimensions[0]}
          >
            {([source?.webM?.filename, source?.mp4.filename].filter(Boolean) as string[]).map((src) => (
              <source key={src} src={src} type={`video/${src.split('.').pop()?.toLowerCase()}`} />
            ))}
            {altText || 'Video'}
            <track default kind="captions" src="" />
          </video>
        </div>
      )}
    </>
  );
};
